import { useState, useEffect } from 'react';
import { Notepad, Plus, X, ArrowRight, CheckCircle, Warning, Circle, ArrowsClockwise, Trash } from '@phosphor-icons/react';
import api from '../api';
import WorkflowActions from '../components/WorkflowActions';
import { useHighlight } from '../hooks/useHighlight';
import { useAuth } from '../contexts/AuthContext';
import { useAriaContext } from '../contexts/AriaContext';
import { useWorkflowDraft } from '../hooks/useWorkflowDraft';
import CustomerLocationCombobox from '../components/CustomerLocationCombobox';

const fmt = n => {
  const v = Number(n) || 0;
  return v >= 100000 ? `₹${(v / 100000).toFixed(1)}L` : `₹${(v / 1000).toFixed(1)}K`;
};

const CURRENCY_SYMBOLS = { INR: '₹', USD: '$', EUR: '€', GBP: '£', AED: 'د.إ' };
const fmtCurrency = (amount, currency = 'INR') => {
  const v = Number(amount) || 0;
  const sym = CURRENCY_SYMBOLS[currency] || currency + ' ';
  return v >= 100000 ? `${sym}${(v / 100000).toFixed(1)}L` : `${sym}${(v / 1000).toFixed(1)}K`;
};

const INP = {
  background: 'hsl(0,0%,7%)', border: '1px solid hsl(0,0%,18%)',
  padding: '9px 12px', color: 'hsl(0,0%,90%)',
  fontFamily: "'JetBrains Mono', monospace", fontSize: 12,
  outline: 'none', width: '100%', boxSizing: 'border-box',
};
const LBL = {
  fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'hsl(0,0%,45%)',
  textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 4, display: 'block',
};

function DispatchProgress({ order }) {
  const showStatuses = new Set(['confirmed', 'in_production', 'partially_dispatched', 'ready_to_dispatch']);
  if (!showStatuses.has(order.status)) return null;

  const orderedQty = order.line_items?.length
    ? order.line_items.reduce((s, i) => s + (i.quantity || 0), 0)
    : (order.quantity || 0);
  const dispatchedQty = order.dispatched_qty || 0;
  if (orderedQty === 0) return null;

  const pct = Math.min(100, Math.round((dispatchedQty / orderedQty) * 100));

  return (
    <div data-testid="dispatch-progress" style={{ marginTop: 3 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8,
          color: 'hsl(0,0%,45%)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
          dispatched
        </span>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, color: 'hsl(0,0%,60%)' }}>
          {dispatchedQty} / {orderedQty}
        </span>
      </div>
      <div style={{ height: 2, background: 'hsl(0,0%,12%)', width: '100%' }}>
        <div style={{ height: 2, width: `${pct}%`,
          background: pct === 100 ? 'hsl(142,60%,45%)' : 'hsl(43,96%,56%)' }} />
      </div>
    </div>
  );
}

function StatusChip({ status }) {
  const map = {
    pending: 'chip-neutral', confirmed: 'chip-info',
    in_production: 'chip-warning', partially_dispatched: 'chip-warning',
    ready_to_dispatch: 'chip-hold',
    dispatched: 'chip-active', cancelled: 'chip-danger',
  };
  return <span className={map[status] || 'chip-neutral'}>{status?.replace(/_/g, ' ').toUpperCase()}</span>;
}

function StockDot({ stockStatus, dispatchReady }) {
  if (dispatchReady) {
    return (
      <span data-testid="dispatch-ready-badge"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 6px',
          background: 'hsl(142,60%,10%)', border: '1px solid hsl(142,60%,30%)',
          fontFamily: "'JetBrains Mono', monospace", fontSize: 8, color: 'hsl(142,70%,55%)',
          textTransform: 'uppercase', letterSpacing: '0.1em', whiteSpace: 'nowrap' }}>
        <CheckCircle size={8} weight="fill" color="hsl(142,70%,55%)" />
        DISPATCH READY
      </span>
    );
  }
  const colorMap = {
    available: 'hsl(142,70%,50%)',
    partial: 'hsl(43,96%,56%)',
    none: 'hsl(0,84%,60%)',
    manual_hold: 'hsl(0,0%,45%)',
  };
  const color = colorMap[stockStatus] || 'hsl(0,0%,35%)';
  const label = stockStatus === 'partial' ? 'PARTIAL' : stockStatus === 'none' ? 'NO STOCK' : stockStatus === 'available' ? 'IN STOCK' : null;
  if (!label) return null;
  return (
    <span data-testid={`stock-status-${stockStatus}`}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 5px',
        fontFamily: "'JetBrains Mono', monospace", fontSize: 8,
        color, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
      <Circle size={6} weight="fill" color={color} />
      {label}
    </span>
  );
}

function CreateOrderModal({ customers, products, onClose, onSaved, prefill }) {
  const emptyLine = () => ({ product_id: '', product_name: '', product_sku: '', quantity: '', unit_price: '' });
  const [form, setForm] = useState({
    customer_id: '', customer_name: '',
    promise_date: '', priority: 'medium', notes: '', currency: 'INR',
    location_id: '', location_nickname: '',
  });
  const [billTo, setBillTo] = useState(null); // { customer_id, customer_name, location_id, location_nickname, location }
  const [lineItems, setLineItems] = useState([emptyLine()]);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Apply prefill from workflow action handoff (customer or product)
  useEffect(() => {
    if (!prefill) return;
    if (prefill.customer_id) {
      const c = customers.find(x => x.id === prefill.customer_id);
      const firstLoc = c?.locations?.[0];
      if (firstLoc) {
        setBillTo({ customer_id: c.id, customer_name: c.name, location_id: firstLoc.loc_id, location_nickname: firstLoc.nickname, location: firstLoc });
      }
      setForm(p => ({
        ...p,
        customer_id: prefill.customer_id,
        customer_name: prefill.customer_name || p.customer_name,
        currency: prefill.currency || p.currency,
        location_id: firstLoc?.loc_id || '',
        location_nickname: firstLoc?.nickname || '',
      }));
    }
    if (prefill.product_id) {
      setLineItems([{
        product_id: prefill.product_id,
        product_name: prefill.product_name || '',
        product_sku: prefill.product_sku || '',
        quantity: '',
        unit_price: prefill.unit_price ? String(prefill.unit_price) : '',
      }]);
    }
  }, [prefill, customers]);

  const handleLocationChange = (val) => {
    setBillTo(val);
    setForm(p => ({
      ...p,
      customer_id: val?.customer_id || '',
      customer_name: val?.customer_name || '',
      location_id: val?.location_id || '',
      location_nickname: val?.location_nickname || '',
      currency: customers.find(c => c.id === val?.customer_id)?.currency || p.currency,
    }));
  };

  const setLine = (idx, k, v) => setLineItems(prev => {
    const updated = [...prev];
    updated[idx] = { ...updated[idx], [k]: v };
    return updated;
  });

  const handleProductChange = async (idx, pid) => {
    const p = products.find(x => x.id === pid);
    setLine(idx, 'product_id', pid);
    setLine(idx, 'product_name', p?.name || '');
    setLine(idx, 'product_sku', p?.sku || '');
    if (p?.selling_price) setLine(idx, 'unit_price', String(p.selling_price));
  };

  const addLine = () => setLineItems(prev => [...prev, emptyLine()]);
  const removeLine = (idx) => setLineItems(prev => prev.filter((_, i) => i !== idx));

  const orderTotal = lineItems.reduce((s, li) =>
    s + (parseFloat(li.quantity) || 0) * (parseFloat(li.unit_price) || 0), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const filledLines = lineItems.filter(li => li.product_id && li.quantity && li.unit_price);
      if (filledLines.length === 0) { alert('Add at least one product line.'); setSaving(false); return; }
      const payload = {
        customer_id: form.customer_id,
        customer_name: form.customer_name,
        product_id: filledLines[0].product_id,
        product_name: filledLines[0].product_name,
        product_sku: filledLines[0].product_sku,
        quantity: parseInt(filledLines[0].quantity) || 0,
        unit_price: parseFloat(filledLines[0].unit_price) || 0,
        line_items: filledLines.map(li => ({
          product_id: li.product_id,
          product_name: li.product_name,
          product_sku: li.product_sku,
          quantity: parseInt(li.quantity) || 0,
          unit_price: parseFloat(li.unit_price) || 0,
          total: (parseInt(li.quantity) || 0) * (parseFloat(li.unit_price) || 0),
        })),
        promise_date: form.promise_date,
        priority: form.priority,
        notes: form.notes,
        currency: form.currency,
        location_id: form.location_id,
        location_nickname: form.location_nickname,
      };
      await api.post('/orders', payload);
      onSaved();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const CURRENCIES = ['INR', 'USD', 'EUR', 'GBP', 'AED'];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 300,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, overflowY: 'auto' }}>
      <div style={{ background: 'hsl(0,0%,5%)', border: '1px solid hsl(0,0%,20%)',
        width: '100%', maxWidth: 680, padding: 24, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 16,
            color: 'hsl(0,0%,98%)', textTransform: 'uppercase' }}>New Sales Order</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none',
            cursor: 'pointer', color: 'hsl(0,0%,50%)' }}><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          {/* Customer / Location + Currency */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div style={{ gridColumn: '1/3' }}>
              <label style={LBL}>Customer & Location *</label>
              <CustomerLocationCombobox
                customers={customers}
                value={billTo}
                onChange={handleLocationChange}
                placeholder="Type customer or location…"
              />
            </div>
            <div>
              <label style={LBL}>Currency</label>
              <select value={form.currency} onChange={e => set('currency', e.target.value)}
                style={{ ...INP, appearance: 'auto' }}>
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          {/* Line Items */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <label style={LBL}>Line Items *</label>
              <button type="button" onClick={addLine}
                style={{ padding: '3px 10px', background: 'transparent',
                  border: '1px solid hsl(197,100%,35%)', color: 'hsl(197,100%,48%)',
                  fontFamily: "'JetBrains Mono', monospace", fontSize: 9, cursor: 'pointer',
                  textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                + Add Line
              </button>
            </div>
            {lineItems.map((li, idx) => (
              <div key={idx} style={{ display: 'grid', gridTemplateColumns: '2fr 80px 100px 32px',
                gap: 8, marginBottom: 8, alignItems: 'end' }}>
                <div>
                  {idx === 0 && <label style={LBL}>Product</label>}
                  <select value={li.product_id} onChange={e => handleProductChange(idx, e.target.value)}
                    style={{ ...INP, appearance: 'auto' }}>
                    <option value="">Select Product</option>
                    {products.filter(p => p.status === 'active').map(p =>
                      <option key={p.id} value={p.id}>{p.name} ({p.sku})</option>
                    )}
                  </select>
                </div>
                <div>
                  {idx === 0 && <label style={LBL}>Qty</label>}
                  <input type="number" min="1" value={li.quantity}
                    onChange={e => setLine(idx, 'quantity', e.target.value)}
                    placeholder="Qty" style={INP} />
                </div>
                <div>
                  {idx === 0 && <label style={LBL}>Unit Price</label>}
                  <input type="number" step="0.01" min="0" value={li.unit_price}
                    onChange={e => setLine(idx, 'unit_price', e.target.value)}
                    placeholder="Price" style={INP} />
                </div>
                <div style={{ paddingBottom: 1 }}>
                  {lineItems.length > 1 && (
                    <button type="button" onClick={() => removeLine(idx)}
                      style={{ width: 32, height: 36, background: 'transparent',
                        border: '1px solid hsl(0,0%,20%)', color: 'hsl(0,84%,60%)',
                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Trash size={12} />
                    </button>
                  )}
                </div>
              </div>
            ))}
            {orderTotal > 0 && (
              <div style={{ padding: '8px 12px', background: 'hsl(0,0%,7%)',
                border: '1px solid hsl(0,0%,14%)', fontFamily: "'JetBrains Mono', monospace",
                fontSize: 11, color: 'hsl(0,0%,60%)', textAlign: 'right' }}>
                Order Total:{' '}
                <span style={{ color: 'hsl(197,100%,48%)', fontWeight: 600 }}>
                  {form.currency} {orderTotal.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                </span>
                {lineItems.filter(li => li.product_id).length > 1 &&
                  <span style={{ color: 'hsl(0,0%,45%)', marginLeft: 8 }}>
                    ({lineItems.filter(li => li.product_id).length} lines)
                  </span>
                }
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 20 }}>
            <div>
              <label style={LBL}>Promise Date *</label>
              <input type="date" value={form.promise_date}
                onChange={e => set('promise_date', e.target.value)} required style={INP} />
            </div>
            <div>
              <label style={LBL}>Priority</label>
              <select value={form.priority} onChange={e => set('priority', e.target.value)}
                style={{ ...INP, appearance: 'auto' }}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
            <div>
              <label style={LBL}>Notes</label>
              <input value={form.notes} onChange={e => set('notes', e.target.value)}
                placeholder="Optional" style={INP} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary">
              {saving ? 'Creating...' : 'Create Order'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function SalesOrders() {
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshResult, setRefreshResult] = useState(null);

  const { user } = useAuth();
  const highlightId = useHighlight();
  const { setVisibleIds } = useAriaContext();
  const { prefill } = useWorkflowDraft('orders', 'create_order');

  // Auto-open create modal when arriving via workflow action
  useEffect(() => {
    if (prefill) setShowCreate(true);
  }, [prefill]);

  const load = async () => {
    try {
      const [oR, cR, pR] = await Promise.all([
        api.get('/orders'), api.get('/customers'), api.get('/products'),
      ]);
      setOrders(oR.data);
      setVisibleIds((oR.data || []).slice(0, 20).map(o => o.order_no));
      setCustomers(cR.data);
      setProducts(pR.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleRefreshStock = async () => {
    setRefreshing(true);
    setRefreshResult(null);
    try {
      const r = await api.post('/orders/refresh-stock-statuses');
      setRefreshResult(r.data);
      await load();
      setTimeout(() => setRefreshResult(null), 5000);
    } catch (e) { console.error(e); }
    setRefreshing(false);
  };

  useEffect(() => { load(); }, []);
  useEffect(() => () => setVisibleIds([]), []); // eslint-disable-line

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400 }}>
      <div style={{ color: 'hsl(197,100%,48%)', fontFamily: "'JetBrains Mono', monospace",
        fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.2em' }}>Loading...</div>
    </div>
  );

  const now = new Date();
  const thisMonth = orders.filter(o => {
    const d = new Date(o.created_at || 0);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });

  const metrics = [
    { label: 'Open Orders', val: orders.filter(o => ['pending', 'confirmed'].includes(o.status)).length, color: 'hsl(197,100%,48%)' },
    { label: 'In Production', val: orders.filter(o => o.status === 'in_production').length, color: 'hsl(43,96%,56%)' },
    { label: 'Ready to Dispatch', val: orders.filter(o => o.status === 'ready_to_dispatch').length, color: 'hsl(12,90%,55%)' },
    { label: 'This Month Revenue', val: fmt(thisMonth.reduce((s, o) => s + (o.total_value || 0), 0)), color: 'hsl(140,60%,45%)' },
  ];

  const STATUS_TABS = ['all', 'pending', 'confirmed', 'in_production', 'partially_dispatched', 'ready_to_dispatch', 'dispatched'];
  const filtered = statusFilter === 'all' ? orders : orders.filter(o => o.status === statusFilter);

  const MONO_SM = { fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
    color: 'hsl(0,0%,50%)', textTransform: 'uppercase', letterSpacing: '0.12em' };

  return (
    <div data-testid="sales-orders-page">
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 22, fontWeight: 700,
            color: 'hsl(0,0%,98%)', textTransform: 'uppercase', marginBottom: 4 }}>Sales Orders</h1>
          <div style={MONO_SM}>Create · Track · Manage All Orders</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {refreshResult && (
            <div data-testid="refresh-stock-result" style={{ padding: '5px 12px',
              background: 'hsl(142,50%,8%)', border: '1px solid hsl(142,60%,25%)',
              fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'hsl(142,70%,55%)' }}>
              {refreshResult.dispatch_ready_count} / {refreshResult.refreshed} dispatch ready
            </div>
          )}
          <button data-testid="refresh-stock-btn" onClick={handleRefreshStock}
            disabled={refreshing}
            style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px',
              background: 'transparent', border: '1px solid hsl(197,100%,35%)',
              color: 'hsl(197,100%,48%)', cursor: refreshing ? 'not-allowed' : 'pointer',
              fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
              textTransform: 'uppercase', letterSpacing: '0.08em', opacity: refreshing ? 0.6 : 1,
              transition: 'all 0.15s' }}>
            <ArrowsClockwise size={12} weight="bold"
              style={{ animation: refreshing ? 'spin 0.8s linear infinite' : 'none' }} />
            {refreshing ? 'Refreshing...' : 'Refresh Stock'}
          </button>
          <button data-testid="create-order-btn" onClick={() => setShowCreate(true)} className="btn-primary"
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Plus size={14} weight="bold" /> New Order
          </button>
        </div>
      </div>

      <div className="metrics-row" style={{ marginBottom: 1, gap: 1 }}>
        {metrics.map(m => (
          <div key={m.label} className="metric-card animate-fadeInUp" style={{ flex: 1, minWidth: 160 }}>
            <div style={MONO_SM}>{m.label}</div>
            <div style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 24, fontWeight: 700,
              color: m.color, marginTop: 8, letterSpacing: '-0.02em' }}>{m.val}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 1, marginTop: 1, marginBottom: 1, overflowX: 'auto' }}>
        {STATUS_TABS.map(s => (
          <button key={s} onClick={() => setStatusFilter(s)} data-testid={`filter-${s}`}
            style={{
              padding: '7px 14px', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
              background: statusFilter === s ? 'hsl(197,100%,48%)' : 'hsl(0,0%,5%)',
              color: statusFilter === s ? '#fff' : 'hsl(0,0%,50%)',
              fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
              textTransform: 'uppercase', letterSpacing: '0.1em',
              borderRight: '1px solid hsl(0,0%,14%)', transition: 'all 0.15s',
            }}>
            {s === 'all' ? `All (${orders.length})` : s.replace(/_/g, ' ')}
          </button>
        ))}
      </div>

      <div style={{ background: 'hsl(0,0%,5%)', border: '1px solid hsl(0,0%,14%)', marginTop: 1 }}>
        <div className="table-scroll">
          <table className="erp-table" data-testid="orders-list-table">
            <thead>
              <tr>
                <th>Order No</th><th>Customer</th><th>Product</th>
                <th>Qty</th><th>Value</th><th>Promise Date</th>
                <th>Priority</th><th>Stock</th><th>Status</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={10} style={{ textAlign: 'center', color: 'hsl(0,0%,35%)', padding: 32 }}>
                  No orders found</td></tr>
              )}
              {filtered.map(o => {
                const days = Math.ceil((new Date(o.promise_date) - new Date()) / 86400000);
                const atRisk = days <= 3 && ['pending', 'confirmed', 'in_production'].includes(o.status);
                const isHighlighted = highlightId && (o.id === highlightId || o.order_no === highlightId);
                return (
                  <tr key={o.id} data-testid={`so-row-${o.order_no}`}
                    style={isHighlighted ? { outline: '1px solid hsl(75,100%,50%)', boxShadow: 'inset 0 0 0 1px hsl(75,100%,50%)' } : {}}>
                    <td style={{ color: 'hsl(197,100%,48%)', fontWeight: 600 }}>{o.order_no}</td>
                    <td>{o.customer_name}</td>
                    <td style={{ color: 'hsl(0,0%,70%)', maxWidth: 180, overflow: 'hidden',
                      textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.product_sku}</td>
                    <td>{(o.quantity || 0).toLocaleString()}</td>
                    <td style={{ fontWeight: 600 }}>{fmtCurrency(o.total_value, o.currency)}</td>
                    <td style={{ color: atRisk ? 'hsl(0,84%,60%)' : 'hsl(0,0%,70%)' }}>
                      {o.promise_date ? new Date(o.promise_date).toLocaleDateString('en-IN',
                        { day: '2-digit', month: 'short' }) : '—'}
                      {atRisk && <span style={{ marginLeft: 6, fontSize: 9, color: 'hsl(0,84%,60%)' }}>AT RISK</span>}
                    </td>
                    <td>
                      <span className={o.priority === 'high' ? 'chip-danger' :
                        o.priority === 'medium' ? 'chip-warning' : 'chip-neutral'}>
                        {(o.priority || 'low').toUpperCase()}
                      </span>
                    </td>
                    <td>
                      <StockDot stockStatus={o.stock_status} dispatchReady={o.dispatch_ready} />
                    </td>
                    <td>
                      <StatusChip status={o.status} />
                      <DispatchProgress order={o} />
                    </td>
                    <td>
                      <WorkflowActions recordType="order" record={o} userRole={user?.role} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showCreate && (
        <CreateOrderModal customers={customers} products={products}
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); load(); }}
          prefill={prefill} />
      )}
    </div>
  );
}
